import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "@/components/Logo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye, BookOpen, Lock, ChevronRight, AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public preview of a whole course — no login required.
 *  Shows the landing page and a list of every published module. */
export default function PublicCoursePreview() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/c/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Link no longer active"));
  }, [token]);

  if (err) return <ShareErrorScreen message={err} />;
  if (!data) return <ShareLoadingScreen />;
  const { course, modules } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <PreviewBanner />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Logo className="h-12 mb-8" />
        <div className="mb-2 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#E53935] font-bold">
          <BookOpen size={13}/> Course preview
        </div>
        <h1 className="text-4xl md:text-5xl font-display font-extrabold text-slate-900 mb-3" data-testid="public-course-title">{course.title}</h1>
        {course.description && <p className="text-slate-600 text-lg leading-relaxed mb-10 max-w-2xl">{course.description}</p>}

        <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">{modules.length} module{modules.length === 1 ? "" : "s"}</h2>
        <div className="space-y-2">
          {modules.length === 0 && (
            <Card className="p-8 text-center text-slate-500">No published modules yet.</Card>
          )}
          {modules.map((m, i) => (
            <button
              key={m.id}
              onClick={() => nav(`/share/c/${token}/m/${m.id}`)}
              className="w-full flex items-center gap-4 p-5 bg-white border border-slate-200 hover:border-[#E53935] rounded-lg text-left transition-colors group"
              data-testid={`public-module-row-${i}`}
            >
              <div className="w-10 h-10 rounded-full bg-slate-100 group-hover:bg-[#E53935] group-hover:text-white text-slate-600 flex items-center justify-center font-bold transition-colors">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-slate-900 group-hover:text-[#E53935] transition-colors">{m.title}</div>
                <div className="text-xs text-slate-500 mt-0.5 font-mono uppercase tracking-wider">{m.artifact_type}</div>
              </div>
              <ChevronRight size={18} className="text-slate-400 group-hover:text-[#E53935] transition-colors"/>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PreviewBanner() {
  return (
    <div className="bg-[#222222] text-white text-center text-xs py-2 px-4 flex items-center justify-center gap-2" data-testid="public-preview-banner">
      <Eye size={12} className="text-[#E53935]"/>
      <span className="font-mono uppercase tracking-[0.2em]">Read-only preview</span>
      <span className="text-slate-400">·</span>
      <span className="text-slate-300">Quiz answers are visible but cannot be submitted</span>
    </div>
  );
}

export function ShareErrorScreen({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
      <Card className="p-10 max-w-md text-center border-slate-200" data-testid="public-share-error">
        <AlertTriangle className="mx-auto text-amber-500 mb-3" size={40} strokeWidth={1.5}/>
        <h2 className="text-2xl font-display font-bold text-slate-900 mb-2">Link expired</h2>
        <p className="text-slate-500 mb-6">{message}</p>
        <Link to="/login">
          <Button variant="outline" className="text-sm">
            <Lock size={13} className="mr-1.5"/> Sign in instead
          </Button>
        </Link>
      </Card>
    </div>
  );
}

export function ShareLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="animate-spin text-[#E53935]" size={28}/>
    </div>
  );
}
